import React, { useEffect, useState, useRef } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { Check, Loader2, XCircle } from "lucide-react";
import { useCart } from "../context/CartContext";
import { getPaymentStatus } from "../lib/api";
import { eur, waLink } from "../config";
import { WhatsAppIcon } from "../components/WhatsAppButton";

const MAX_TRIES = 8;

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const { clearCart } = useCart();
  const navigate = useNavigate();
  const [state, setState] = useState("checking");
  const [order, setOrder] = useState(null);
  const cleared = useRef(false);

  useEffect(() => {
    if (!sessionId) { setState("error"); return; }
    let tries = 0;
    let timer;
    const poll = async () => {
      try {
        const r = await getPaymentStatus(sessionId);
        if (r.payment_status === "paid") {
          setOrder(r.order || { order_number: r.order_number, total: r.total });
          setState("paid");
          if (!cleared.current) { cleared.current = true; clearCart(); }
          return;
        }
        if (["expired", "failed"].includes(r.payment_status)) { setState("error"); return; }
      } catch { /* keep polling */ }
      tries += 1;
      if (tries >= MAX_TRIES) { setState("timeout"); return; }
      timer = setTimeout(poll, 2000);
    };
    poll();
    return () => clearTimeout(timer);
  }, [sessionId, clearCart]);

  if (state === "checking") {
    return (
      <div className="min-h-[60vh] flex flex-col items-center justify-center text-center px-6" data-testid="payment-checking">
        <Loader2 size={44} className="text-dore animate-spin mb-4" />
        <p className="font-serif-el italic text-dore text-xl">Vérification de votre paiement...</p>
        <p className="text-ardoise text-sm mt-2">Merci de patienter quelques instants.</p>
      </div>
    );
  }

  if (state === "paid") {
    return (
      <div className="max-w-xl mx-auto px-6 py-24 text-center" data-testid="payment-success">
        <div className="w-16 h-16 rounded-full bg-dore/20 flex items-center justify-center mx-auto mb-6"><Check size={32} className="text-dore" /></div>
        <h1 className="font-display text-3xl text-encre">Paiement confirmé !</h1>
        <p className="text-ardoise mt-3">Merci pour votre commande <b className="text-encre" data-testid="success-order-number">{order?.order_number}</b>{order?.total ? ` — ${eur(order.total)}` : ""}. Un email de confirmation vous a été envoyé. Nous préparons votre colis pour la Guadeloupe.</p>
        <div className="flex flex-col sm:flex-row gap-3 justify-center mt-8">
          <button onClick={() => navigate("/boutique")} className="bg-noir text-white px-8 py-3.5 text-xs uppercase tracking-widest hover:bg-ardoise transition-colors">Continuer mes achats</button>
          <button onClick={() => navigate("/compte")} className="border border-dore text-encre px-8 py-3.5 text-xs uppercase tracking-widest hover:bg-dore hover:text-white transition-colors">Voir mes commandes</button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-xl mx-auto px-6 py-24 text-center" data-testid="payment-error">
      <div className="w-16 h-16 rounded-full bg-rose/40 flex items-center justify-center mx-auto mb-6"><XCircle size={32} className="text-encre" /></div>
      <h1 className="font-display text-3xl text-encre">{state === "timeout" ? "Paiement en cours de traitement" : "Un souci est survenu"}</h1>
      <p className="text-ardoise mt-3">{state === "timeout" ? "Votre paiement est en cours de confirmation. Vous recevrez un email dès validation." : "Nous n'avons pas pu confirmer votre paiement. Vous pouvez réessayer ou nous contacter."}</p>
      <div className="flex flex-col sm:flex-row gap-3 justify-center mt-8">
        <Link to="/commande" className="bg-noir text-white px-8 py-3.5 text-xs uppercase tracking-widest hover:bg-ardoise transition-colors">Retour au panier</Link>
        <a href={waLink("Bonjour ! J'ai une question sur mon paiement.")} target="_blank" rel="noreferrer" className="border border-whatsapp text-whatsapp px-8 py-3.5 text-xs uppercase tracking-widest flex items-center justify-center gap-2 hover:bg-whatsapp hover:text-white transition-colors"><WhatsAppIcon size={15} /> Nous contacter</a>
      </div>
    </div>
  );
}
